import { useEffect, useMemo, useState } from 'react';
import { apiJson, fetchAuthedBlob } from '../api/client.js';
import { Modal, ModalConfirmFooter } from './Modal.jsx';
import { formatDateTime } from '../utils/formatDate.js';

const VIEW_KEY = 'project-media-view';
const FILTERS = [
  { id: 'all', label: 'All files' },
  { id: 'image', label: 'Images' },
  { id: 'document', label: 'Documents' },
  { id: 'video', label: 'Videos' },
  { id: 'other', label: 'Other' },
];
const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'name', label: 'Name A–Z' },
  { id: 'type', label: 'File type' },
];

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function typeMark(item) {
  const name = String(item.file_name || item.label || '').toLowerCase();
  const mime = String(item.file_mime || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (name.endsWith('.doc') || name.endsWith('.docx') || mime.includes('word')) return 'DOC';
  if (name.endsWith('.xls') || name.endsWith('.xlsx') || mime.includes('sheet') || mime.includes('excel')) return 'XLS';
  if (item.file_kind === 'video') return 'VIDEO';
  if (item.file_kind === 'image') return 'IMG';
  return 'FILE';
}

function visibilityLabel(value) {
  return value === 'shared' ? 'Shared with client' : 'Internal only';
}

async function saveBlob(url, name) {
  const blob = await fetchAuthedBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = name || 'download';
  link.click();
  URL.revokeObjectURL(objectUrl);
}

function MediaPreview({ item, onOpen }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (item.preview !== 'image' || !item.thumbnail_url) {
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    fetchAuthedBlob(item.thumbnail_url)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id, item.preview, item.thumbnail_url]);

  const open = () => onOpen(item);

  if (item.preview === 'missing') {
    return (
      <button type="button" className="media-preview media-preview--missing" onClick={open}>
        <span>File missing</span>
      </button>
    );
  }

  if (src) {
    return (
      <button type="button" className="media-preview" onClick={open}>
        <img src={src} alt="" />
      </button>
    );
  }

  return (
    <button type="button" className="media-preview" onClick={open}>
      {item.preview === 'image' && !failed ? (
        <span className="muted small">Loading…</span>
      ) : (
        <span className={`media-type-icon media-type-icon--${item.file_kind || 'other'}`}>{failed ? 'No preview' : typeMark(item)}</span>
      )}
    </button>
  );
}

function Lightbox({ items, index, onClose, onIndex }) {
  const item = items[index];
  const [src, setSrc] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') onIndex(Math.min(items.length - 1, index + 1));
      if (event.key === 'ArrowLeft') onIndex(Math.max(0, index - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndex]);

  useEffect(() => {
    if (!item || item.preview !== 'image' || !item.file_url) {
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    setSrc('');
    setError('');
    fetchAuthedBlob(item.file_url)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Preview is not available for this file.');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item]);

  if (!item) return null;

  const download = () => {
    if (!item.file_url) return;
    saveBlob(item.file_url, item.file_name || item.label).catch((err) => setError(err.message));
  };

  return (
    <div className="media-lightbox" role="dialog" aria-modal="true" aria-label={item.label || item.file_name}>
      <div className="media-lightbox-bar">
        <strong>{item.label || item.file_name}</strong>
        <div className="media-lightbox-actions">
          <button type="button" className="btn-secondary btn-sm" disabled={index <= 0} onClick={() => onIndex(index - 1)}>
            Previous
          </button>
          <button type="button" className="btn-secondary btn-sm" disabled={index >= items.length - 1} onClick={() => onIndex(index + 1)}>
            Next
          </button>
          <button type="button" className="btn-primary btn-sm" disabled={!item.file_url} onClick={download}>
            Download
          </button>
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div className="media-lightbox-stage">
        {item.preview === 'missing' || !item.file_available ? (
          <p className="media-lightbox-note">This file is no longer in storage. Refresh the library, or ask an admin to check that the stored file still exists.</p>
        ) : error ? (
          <p className="media-lightbox-note">{error}</p>
        ) : item.preview === 'image' ? (
          src ? <img src={src} alt={item.label || item.file_name || ''} /> : <p className="media-lightbox-note">Loading…</p>
        ) : (
          <div className="media-lightbox-file">
            <span className={`media-type-icon media-type-icon--${item.file_kind || 'other'}`}>{typeMark(item)}</span>
            <p>{item.file_name}</p>
            {item.file_size ? <p className="muted small">{formatFileSize(item.file_size)}</p> : null}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   items: Array<any>;
 *   projectId: string | number;
 *   mode: 'staff' | 'client';
 *   canManage?: boolean;
 *   pending?: Array<any>;
 *   onRefresh: () => void | Promise<void>;
 * }} props
 */
export function ProjectMediaLibrary({ items, projectId, mode, canManage = false, pending = [], onRefresh }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
  const [menuId, setMenuId] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    if (menuId == null) return undefined;
    const close = () => setMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuId]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const next = (items || []).filter((item) => {
      if (filter !== 'all' && item.file_kind !== filter) return false;
      if (!needle) return true;
      const hay = `${item.label || ''} ${item.file_name || ''}`.toLowerCase();
      return hay.includes(needle);
    });
    next.sort((a, b) => {
      if (sort === 'oldest') return String(a.created_at || '').localeCompare(String(b.created_at || ''));
      if (sort === 'name') return String(a.label || a.file_name || '').localeCompare(String(b.label || b.file_name || ''));
      if (sort === 'type') return String(a.file_kind || '').localeCompare(String(b.file_kind || '')) || String(a.label || '').localeCompare(String(b.label || ''));
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
    return next;
  }, [items, query, filter, sort]);

  const base = mode === 'staff' ? `/api/admin/projects/${projectId}/media` : `/api/my/projects/${projectId}/media`;

  const openPreview = (item) => {
    const index = visible.findIndex((entry) => entry.id === item.id);
    setLightbox(index >= 0 ? index : 0);
    setMenuId(null);
  };

  const download = async (item) => {
    setMenuId(null);
    if (!item.file_url) {
      setActionError('This file is no longer in storage.');
      return;
    }
    try {
      await saveBlob(item.file_url, item.file_name || item.label);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const changeVisibility = async (item) => {
    setMenuId(null);
    setBusy(true);
    setActionError('');
    try {
      await apiJson(`${base}/${item.id}`, {
        method: 'PATCH',
        body: { visibility: item.visibility === 'shared' ? 'internal' : 'shared' },
      });
      await onRefresh();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    setBusy(true);
    setActionError('');
    try {
      await apiJson(`${base}/${renameTarget.id}`, {
        method: 'PATCH',
        body: { label: renameValue.trim() },
      });
      setRenameTarget(null);
      await onRefresh();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setActionError('');
    try {
      await apiJson(`${base}/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await onRefresh();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="media-library">
      <div className="media-toolbar">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by file name"
          aria-label="Search files"
        />
        <div className="media-filters" role="tablist" aria-label="File type">
          {FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={filter === entry.id ? 'is-active' : ''}
              onClick={() => setFilter(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <label className="media-sort">
          <span className="muted small">Sort</span>
          <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort files">
            {SORTS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
        <div className="media-view-toggle" role="group" aria-label="Layout">
          <button type="button" className={view === 'grid' ? 'is-active' : ''} onClick={() => setView('grid')}>
            Grid
          </button>
          <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')}>
            List
          </button>
        </div>
      </div>

      {actionError ? <p className="err small">{actionError}</p> : null}

      {pending.length ? (
        <div className="media-pending">
          <h4>Waiting for review</h4>
          <ul>
            {pending.map((file) => (
              <li key={file.id}>
                <span>{file.label || file.file_name}</span>
                <span className={`badge badge-${file.approval_status === 'rejected' ? 'err' : 'warn'}`}>
                  {file.approval_status === 'rejected' ? 'Rejected' : 'Pending review'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!visible.length ? (
        <p className="muted">{items?.length ? 'No files match this search.' : 'No files in this project yet.'}</p>
      ) : view === 'grid' ? (
        <ul className="media-grid">
          {visible.map((item) => (
            <li key={item.id} className="media-card">
              <MediaPreview item={item} onOpen={openPreview} />
              <div className="media-card-body">
                <div className="media-card-title">
                  <strong title={item.label || item.file_name}>{item.label || item.file_name}</strong>
                  <FileMenu
                    item={item}
                    open={menuId === item.id}
                    canManage={canManage}
                    busy={busy}
                    onToggle={() => setMenuId(menuId === item.id ? null : item.id)}
                    onPreview={() => openPreview(item)}
                    onDownload={() => void download(item)}
                    onRename={() => {
                      setMenuId(null);
                      setRenameTarget(item);
                      setRenameValue(item.label || item.file_name || '');
                    }}
                    onVisibility={() => void changeVisibility(item)}
                    onDelete={() => {
                      setMenuId(null);
                      setDeleteTarget(item);
                    }}
                  />
                </div>
                <div className="muted small media-card-meta">
                  {formatFileSize(item.file_size) || typeMark(item)}
                  {mode === 'staff' ? ` · ${visibilityLabel(item.visibility)}` : ''}
                </div>
                {item.preview === 'missing' ? (
                  <p className="err small">This file is no longer in storage.</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="table-wrap">
          <table className="data-table media-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded by</th>
                <th>Uploaded</th>
                {mode === 'staff' ? <th>Visibility</th> : null}
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.id}>
                  <td>
                    <button type="button" className="linkish" onClick={() => openPreview(item)}>
                      {item.label || item.file_name}
                    </button>
                    {item.preview === 'missing' ? <div className="err small">No longer in storage</div> : null}
                  </td>
                  <td>{typeMark(item)}</td>
                  <td>{formatFileSize(item.file_size) || '—'}</td>
                  <td>{item.uploaded_by?.name || '—'}</td>
                  <td>{item.created_at ? formatDateTime(item.created_at) : '—'}</td>
                  {mode === 'staff' ? <td>{visibilityLabel(item.visibility)}</td> : null}
                  <td>
                    <FileMenu
                      item={item}
                      open={menuId === item.id}
                      canManage={canManage}
                      busy={busy}
                      onToggle={() => setMenuId(menuId === item.id ? null : item.id)}
                      onPreview={() => openPreview(item)}
                      onDownload={() => void download(item)}
                      onRename={() => {
                        setMenuId(null);
                        setRenameTarget(item);
                        setRenameValue(item.label || item.file_name || '');
                      }}
                      onVisibility={() => void changeVisibility(item)}
                      onDelete={() => {
                        setMenuId(null);
                        setDeleteTarget(item);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightbox >= 0 ? (
        <Lightbox items={visible} index={lightbox} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
      ) : null}

      <Modal
        open={Boolean(renameTarget)}
        title="Rename file"
        onClose={() => setRenameTarget(null)}
        footer={(
          <ModalConfirmFooter
            onCancel={() => setRenameTarget(null)}
            onConfirm={() => void submitRename()}
            confirmLabel="Save"
            confirmBusy={busy}
          />
        )}
      >
        <label className="field">
          <span>Name</span>
          <input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
        </label>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        title="Delete file"
        description={deleteTarget ? `Delete “${deleteTarget.label || deleteTarget.file_name}”? This cannot be undone.` : ''}
        onClose={() => setDeleteTarget(null)}
        footer={(
          <ModalConfirmFooter
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => void submitDelete()}
            confirmLabel="Delete"
            confirmBusy={busy}
            danger
          />
        )}
      >
        <p className="muted small">The original file is removed from storage. Clients will no longer be able to open it.</p>
      </Modal>
    </div>
  );
}

function FileMenu({ item, open, canManage, busy, onToggle, onPreview, onDownload, onRename, onVisibility, onDelete }) {
  return (
    <div className="media-menu" onClick={(event) => event.stopPropagation()}>
      <button type="button" className="media-menu-button" aria-label={`Actions for ${item.label || item.file_name}`} aria-expanded={open} onClick={onToggle}>
        ···
      </button>
      {open ? (
        <div className="media-menu-panel" role="menu">
          <button type="button" onClick={onPreview}>Preview</button>
          <button type="button" onClick={onDownload} disabled={!item.file_url}>Download</button>
          {canManage ? (
            <>
              <button type="button" onClick={onRename} disabled={busy}>Rename</button>
              <button type="button" onClick={onVisibility} disabled={busy}>
                {item.visibility === 'shared' ? 'Make internal only' : 'Share with client'}
              </button>
              <button type="button" className="is-danger" onClick={onDelete} disabled={busy}>Delete</button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
